'use client'
import * as React from 'react'
import { cn } from '@/lib/utils'

// CustomerLogo renders a brand logo on a tile that auto-flips between
// light (default) and dark backgrounds. The challenge: many crawled
// logos are white-on-transparent, they vanish on a white tile.
// Filenames are hashed (e.g. 1349af.webp), so a URL-only heuristic
// rarely fires.
//
// Strategy:
//   1. Fast-path: if the URL or sourceUrl mentions "white/light/inverse",
//      flip to dark tile immediately on first paint.
//   2. Robust path: once the image loads, draw it on an offscreen canvas
//      and sample non-transparent pixel brightness. If the visible
//      content is overwhelmingly bright (> 215/255 avg luminance), flip
//      to the dark tile.

const LIGHT_LOGO_RE = /(white|light|inverse|reverse|trans|knockout|_w\.|-w\.)/i

export function isLightLogo(url: string): boolean {
  return LIGHT_LOGO_RE.test(url || '')
}

export function CustomerLogo({
  src,
  alt = '',
  size = 'md',
  sourceUrl,
}: {
  src: string
  alt?: string
  size?: 'sm' | 'md' | 'lg'
  /** Original (pre-hash) URL, gives the brightness heuristic a hint */
  sourceUrl?: string
}) {
  const urlHint = isLightLogo(src) || isLightLogo(sourceUrl || '')
  const [isLight, setIsLight] = React.useState<boolean>(urlHint)

  const sz = size === 'lg' ? { tile: 'h-24 p-4',  img: 'max-h-14' } :
             size === 'sm' ? { tile: 'h-12 p-2',  img: 'max-h-7' }  :
                              { tile: 'h-20 p-3', img: 'max-h-12' }

  // Brightness probe, runs once per src, in an offscreen canvas.
  // If the visible (alpha > 0) pixels are mostly white, switch tile.
  React.useEffect(() => {
    if (isLight) return // already flipped via URL hint, no need to probe
    let cancelled = false
    const probe = new window.Image()
    probe.crossOrigin = 'anonymous'
    probe.decoding = 'async'
    probe.src = src
    probe.onload = () => {
      if (cancelled) return
      try {
        const c = document.createElement('canvas')
        const W = 32, H = 32
        c.width = W; c.height = H
        const ctx = c.getContext('2d')
        if (!ctx) return
        ctx.drawImage(probe, 0, 0, W, H)
        const data = ctx.getImageData(0, 0, W, H).data
        let totalLum = 0, visible = 0
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3]
          if (a < 32) continue // skip transparent
          // BT.601 luma, fast approximation of perceived brightness
          const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
          totalLum += lum
          visible++
        }
        if (visible < 16) return // image not really there
        const avg = totalLum / visible
        if (avg > 215) setIsLight(true)
      } catch {
        // tainted canvas / CORS denied, can't read pixels. Leave tile as-is.
      }
    }
    return () => { cancelled = true }
  }, [src, isLight])

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden rounded-xl border shadow-sm transition',
        sz.tile,
        isLight ? 'bg-slate-900 border-slate-800' : 'bg-gradient-to-br from-white to-slate-50 border-ink-200/40',
      )}
    >
      {/* Using a plain <img> so the brightness probe and the rendered
          element share the same CORS-controlled fetch. next/image with
          the hashed local URL doesn't expose pixel data we need. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={cn(
          'w-auto max-w-full object-contain',
          sz.img,
          'min-w-[60%]',
        )}
      />
    </div>
  )
}
